import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { API_URL, getUserData, needsUserId } from "../config";

// Interface para o usuário
interface UserData {
  userId: number;
  Nome: string;
  Role: string;
}

// Nova interface para o stream
interface StreamMethods {
  prepareStream: (params: StreamParams) => Promise<{
    success: boolean;
    data?: any;
    error?: any;
  }>;
  getHlsUrl: (tenantId: number, cameraId: number) => string;
  checkHlsAvailability: (url: string) => Promise<boolean>;
}

// Interface para parâmetros do stream
interface StreamParams {
  camera_id: number;
  tenant_id?: number;
  recorder_id?: number | null;
  rtsp_url: string;
}

// Estender a interface AxiosInstance para incluir nossa propriedade stream
declare module "axios" {
  interface AxiosInstance {
    stream: StreamMethods;
  }
}


// Função para normalizar caminhos URL e remover barras duplas
function normalizarUrl(url: string): string {
  // Remover barras iniciais
  while (url.startsWith('/')) {
    url = url.substring(1);
  }
  
  // Remover barras duplas no meio da string
  while (url.includes('//')) {
    url = url.replace('//', '/');
  }
  
  return url;
}

// URL base do serviço HLS
export const HLS_BASE_URL = process.env.REACT_APP_HLS_API_URL || `${API_URL}/hls`;

// Cria a instância do Axios com a URL base para proxy
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
});

// Adicione aqui para verificar quando o serviço de API é inicializado
console.log("API URL:", API_URL, "Ambiente:", process.env.NODE_ENV);
console.log("HLS URL:", HLS_BASE_URL);

// Interceptor de requisição para adicionar o token e verificar autenticação
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Normalizar URL para evitar barras duplas
    if (config.url) {
      config.url = normalizarUrl(config.url);
    }

    // Verificar se tem token no localStorage
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");
    
    if (!token || !userStr) {
      console.warn("Tentando fazer requisição sem autenticação completa");
    }
    
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    
    // Adicionar userId como parâmetro nas requisições, se disponível
    if (userStr) {
      try {
        const userData = JSON.parse(userStr) as UserData;
        
        // Verificar se o endpoint precisa do userId
        // Com base nas rotas que você compartilhou
        if (config.url && (
          config.url.includes("tenant") || 
          config.url.includes("role") ||
          config.url.includes("stable") ||
          config.url.includes("stall") ||
          config.url.includes("horse") ||
          config.url.includes("recorder") ||
          config.url.includes("camera") ||
          // Adicionar stream às rotas que precisam de userId
          config.url.includes("stream") ||
          // Parceiro: sem o userId o servidor não tem como aplicar o escopo
          // (a rota não recebe partner no caminho).
          config.url.includes("partner") ||
          // Para "user", adicionar userId apenas se não for uma requisição POST
          (config.url.includes("user") && config.method !== 'post') ||
          false
      )) {
          // Se já tiver query parameters, adiciona userId
          if (config.params) {
            config.params.userId = userData.userId;
          } else {
            // Se não tiver nenhum params, cria um objeto com userId
            config.params = { userId: userData.userId };
          }
        }
      } catch (error) {
        console.error("Erro ao processar dados do usuário:", error);
      }
    }
    
    // URL completa para logging
    const baseURL = config.baseURL || '';
    const url = config.url || '';
    const completeUrl = baseURL && url 
  ? `${baseURL}/${url}`.replace(/([^:])\/+/g, '$1/') // Preserva o protocolo
  : baseURL || url;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de resposta para lidar com erros de autenticação
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    console.error("API Error:", error.response?.status, error.message);

    if (error.response) {
      console.error("Error response data:", error.response.data);
    }

    return Promise.reject(error);
  }
);
// Adicionar métodos de stream ao api
api.stream = {
  // Método para preparar um stream RTSP para conversão em HLS
  prepareStream: async (params: StreamParams) => {
    try {
      console.log("Enviando requisição para conversão RTSP→HLS:", params);
      const response = await api.post('/stream/prepare', params);
      return {
        success: response.status === 200,
        data: response.data
      };
    } catch (error) {
      console.error("Erro ao preparar stream:", error);
      return {
        success: false,
        error
      };
    }
  },
  
  // Método para obter a URL HLS para uma câmera
  getHlsUrl: (tenantId: number, cameraId: number) => {
    return `${HLS_BASE_URL}/camera/${tenantId}/${cameraId}/stream.m3u8`;
  },
  
  // Método para verificar se um stream HLS está disponível
  checkHlsAvailability: async (url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error("Erro ao verificar disponibilidade do HLS:", error);
      return false;
    }
  }
};

export default api;